import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getDealerProfile, updateDealerProfile as updateDealerProfileApi } from "../api/api";
import type { DealerProfile } from "../types/dealer.types";

interface DealerState {
  profile: DealerProfile | null;
  loading: boolean;
  error: string | null;
  isEditProfileModalOpen: boolean;
  isPlanSelectionModalOpen: boolean;
  isSubscriptionModalOpen: boolean;
  previewTier: string | null;
}

const initialState: DealerState = {
  profile: null,
  loading: false,
  error: null,
  isEditProfileModalOpen: false,
  isPlanSelectionModalOpen: false,
  isSubscriptionModalOpen: false,
  previewTier: null,
};

export const fetchDealerProfile = createAsyncThunk(
  "dealer/fetchProfile",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getDealerProfile(); // Returns AxiosResponse
      if (!response || !response.data) {
        throw new Error("Invalid response received");
      }
      // MOCK INJECTION REMOVED - Using real API data
      const data = response.data.data;
      
      // Sanitize phone number if it has leading quotes
      if (data && typeof data.phone === 'string') {
        data.phone = data.phone.replace(/^['"]/, "").trim();
      }
      
      return data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch profile"
      );
    }
  }
);

export const updateDealerProfile = createAsyncThunk(
  "dealer/updateProfile",
  async (data: Partial<DealerProfile>, { rejectWithValue }) => {
    try {
      // Sanitize data before sending to API
      const sanitizedData = { ...data };
      if (typeof sanitizedData.phone === 'string') {
        sanitizedData.phone = sanitizedData.phone.replace(/^['"]/, "").trim();
      }

      const response = await updateDealerProfileApi(sanitizedData);
      if (!response || !response.data) {
        throw new Error("Invalid response received from update");
      }
      
      const result = response.data.data;
      if (result && typeof result.phone === 'string') {
        result.phone = result.phone.replace(/^['"]/, "").trim();
      }

      return result;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to update profile"
      );
    }
  }
);

const dealerSlice = createSlice({
  name: "dealer",
  initialState,
  reducers: {
    clearDealerError: (state) => {
      state.error = null;
    },
    openEditProfileModal: (state) => {
      state.isEditProfileModalOpen = true;
    },
    closeEditProfileModal: (state) => {
      state.isEditProfileModalOpen = false;
    },
    openPlanSelectionModal: (state) => {
      state.isPlanSelectionModalOpen = true;
    },
    closePlanSelectionModal: (state) => {
      state.isPlanSelectionModalOpen = false;
    },
    openSubscriptionModal: (state) => {
      state.isSubscriptionModalOpen = true;
    },
    closeSubscriptionModal: (state) => {
      state.isSubscriptionModalOpen = false;
    },
    setPreviewTier: (state, action) => {
      state.previewTier = action.payload;
    }
  },
  extraReducers: (builder) => {
    // Fetch Profile
    builder
      .addCase(fetchDealerProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDealerProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload;
      })
      .addCase(fetchDealerProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update Profile
    builder
      .addCase(updateDealerProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateDealerProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload;
      })
      .addCase(updateDealerProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearDealerError,
  openEditProfileModal,
  closeEditProfileModal,
  openPlanSelectionModal,
  closePlanSelectionModal,
  openSubscriptionModal,
  closeSubscriptionModal,
  setPreviewTier
} = dealerSlice.actions;
export default dealerSlice.reducer;
